import { Component, StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

/**
 * Last-resort boundary above every provider. Route-level errors (including
 * mid-deploy chunk failures) are handled with more finesse by
 * RouteErrorBoundary; this one only catches crashes in the provider shell
 * itself, so it must not depend on router/theme/query — plain HTML + inline
 * styles only.
 */
class RootErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          padding: 24,
          textAlign: "center",
          // Tokens still resolve here — index.css is loaded before React
          // mounts — so the fallback matches the active theme. The hsl()
          // wrapping is required: the vars hold bare HSL triplets.
          fontFamily: "var(--font-sans)",
          background: "hsl(var(--background))",
          color: "hsl(var(--foreground))",
        }}
      >
        <p
          style={{
            fontSize: 20,
            fontWeight: 600,
            margin: 0,
            fontFamily: "var(--font-display)",
            letterSpacing: "-0.02em",
          }}
        >
          Staff Hub stopped responding
        </p>
        <p style={{ fontSize: 14, color: "hsl(var(--foreground-muted))", margin: 0, maxWidth: 320 }}>
          Reload to get back to Staff Hub. Your clock-ins are saved on this device.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: 8,
            padding: "12px 24px",
            borderRadius: 10,
            border: "none",
            background: "hsl(var(--foreground))",
            color: "hsl(var(--background))",
            fontFamily: "var(--font-sans)",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

createRoot(rootEl).render(
  <StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </StrictMode>,
);
